// Pure helpers for the VQE column pickers and the SELECT / ORDER BY / GROUP BY
// / WHERE / JOIN emitters that write a picked column into the buffer.
//
// A column is referenced through the query source that reads its table
// (`querySources`): the heading shows what the query calls it (alias, else the
// table name) and the SQL uses that source's qualifier as the query spelled it
// (`oi."qty"`, `"order items"."qty"`, `[Order Details].[Qty]`). A column whose
// table is not in the query (the SELECT pickers' "Show all tables") falls back
// to the catalog spelling of the table, delimited when it must be. Display
// text and SQL text are kept apart: the heading is never emitted.
//
// `columnSuggestionKey` produces the stable popover item key; tests assert its
// stability so re-render-on-suggestion-fetch doesn't reset list selection.

import type { SqlEngine } from '../../../../sql-grammar/model';
import { identifierKey } from '../../../../sql-resolver/service/snapshotRelations';
import { catalogIdentSql } from '../../../../sql-resolver/suggest/sqlIdentifierText';
import type { Column } from '../../../../sql-resolver/types/schema';
import { formatIdent } from './fromItemHelpers';
import { findRelationSource, type QuerySource } from '../../../../sql-resolver/scope/querySources';

/** Everything a column reference needs: the engine and the statement's sources (inner first, then outer). */
export type ColumnRefs = {
  readonly engine: SqlEngine;
  readonly sources: readonly QuerySource[];
};

/**
 * The base-table source a column belongs to, by relation identity (schema
 * included — `findRelationSource`), so `mia_s2.orders b`'s columns take `b`,
 * never the alias of a same-named table of another schema.
 */
export function sourceOfColumn(column: Column, refs: ColumnRefs | null | undefined): QuerySource | undefined {
  return refs ? findRelationSource(refs.sources, column.relation) : undefined;
}

/** The user-visible table part: the query's name for the source, else the catalog table name. */
export function columnTableLabel(column: Column, refs: ColumnRefs | null | undefined): string {
  return sourceOfColumn(column, refs)?.exposedName ?? column.relation.name;
}

/** The SQL qualifier for a column: the source's qualifier, else the table's catalog spelling. */
export function columnQualifierSql(column: Column, refs: ColumnRefs): string {
  return sourceOfColumn(column, refs)?.qualifierSql ?? catalogIdentSql(column.relation.name, refs.engine);
}

/** The column's own name as SQL (catalog spelling). */
export function columnNameSql(column: Column, engine: SqlEngine): string {
  return catalogIdentSql(column.qualified.name, engine);
}

/**
 * The SQL an "add column" edit injects: `qualifier.column` with both parts
 * dialect-correct. Without refs (no statement context) the bare column name,
 * honouring its `quoted` marker.
 */
export function formatColumnRef(column: Column, refs: ColumnRefs | null | undefined): string {
  if (!refs) return formatIdent(column.qualified.name, column.qualified.quoted);
  return `${columnQualifierSql(column, refs)}.${columnNameSql(column, refs.engine)}`;
}

/** `table.column`, folded — `identifierKey`, so `"a.b"."c"` and `"a"."b.c"` never share a key. */
export function columnSuggestionKey(column: Column): string {
  return identifierKey(column.relation.foldedName, column.qualified.foldedName);
}
